import { StorageProvider } from './types';

/**
 * SessionStorage provider implementation
 */
export class SessionStorageProvider implements StorageProvider {
  /**
   * Get a value from sessionStorage
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const value = sessionStorage.getItem(key);
      return value ? JSON.parse(value) as T : null;
    } catch (error) {
      console.error('Error retrieving from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Store a value in sessionStorage
   * @param key The key to store the value under
   * @param value The value to store
   */
  async put<T>(key: string, value: T): Promise<void> {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error storing in sessionStorage:', error);
      throw error;
    }
  }

  /**
   * Remove a value from sessionStorage
   * @param key The key to remove
   */
  async remove(key: string): Promise<void> {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from sessionStorage:', error);
      throw error;
    }
  }

  /**
   * Check if a key exists in sessionStorage
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  async has(key: string): Promise<boolean> {
    return sessionStorage.getItem(key) !== null;
  }
  
  /**
   * Get the storage type name
   */
  getStorageType(): string {
    return 'sessionStorage';
  }
}